import React, {useState} from "react";
import { Select, SelectItem, VbButton } from "@/components/vb";
import { Send, RotateCw, CalendarClock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {ShareReportDialog} from "@/components/projects/components/SharedReportsDialog.tsx";
import type { DashboardPeriod } from "@/lib/reportPeriods";

/** Report-scoped actions, absorbed into the dashboard page header (AITS-490).
 *  The period selector + Share + Re-run + Schedule that used to sit in their own
 *  toolbar row now live in the header's right cluster.
 *
 *  AITS-490 hand-rolled the neutral `.btn` look here in Tailwind utilities
 *  because `.vb-btn` and the shared Select were `.vb-root`-scoped and the report
 *  shell is not under `.vb-root`. AITS-494 promoted `.vb-btn` out of that scope,
 *  so this now uses the shared primitives (AITS-501) — same look, one
 *  implementation. */

// Icon-only control: the shared button's square modifier.
const ICON_BTN = "vb-btn--icon";

interface SignalsActionsProps {
    periods: DashboardPeriod[];
    selectedMonth: string;
    selectedYear: string;
    onMonthChange: (month: string) => void;
    onYearChange: (year: string) => void;
    isFreeUser: boolean
    reportId: number;
    companyId?: number | null;
    companyName?: string | null;
    showReRunButton?: boolean;
    onReRun?: () => void;
    showScheduleButton?: boolean;
    onSchedule?: () => void;
}

export const SignalsActions: React.FC<SignalsActionsProps> = ({
                                                                  periods,
                                                                  selectedMonth,
                                                                  selectedYear,
                                                                  onMonthChange,
                                                                  onYearChange,
                                                                  isFreeUser,
                                                                  reportId,
                                                                  companyId,
                                                                  companyName,
                                                                  showReRunButton = false,
                                                                  onReRun,
                                                                  showScheduleButton = false,
                                                                  onSchedule,
                                                              }) => {
    // Only months with a report you can actually open. A month with nothing
    // finished — never run, still building, or errored — is not a destination:
    // picking it showed the same "generate this month" prompt the dashboard
    // already renders permanently, over the previous month's data.
    const realPeriods = periods.filter((p) => p.viewableId !== null);
    const years = Array.from(new Set(realPeriods.map((p) => p.year)));
    const filteredMonths = realPeriods
        .filter((p) => p.year === selectedYear)
        .map((p) => p.month);
    const [dialogOpen, setDialogOpen] = useState(false);
    const handleYearChange = (year: string) => {
        onYearChange(year);
        const available = realPeriods.find((p) => p.year === year);
        onMonthChange(available ? available.month : "");
    };

    return (
        <div className="flex items-center gap-2 flex-wrap">
            {/* Period — the month/year pair doubles as "past reports" navigation.
                Not rendered when the company has no report yet: two empty
                dropdowns offering nothing is worse than no control at all.
                (Conditional render, not the `hidden` attribute — `display:flex`
                from the class overrides it.) */}
            {realPeriods.length > 0 ? (
            <div className="flex gap-2">
                {/* Width lives on the wrapper, not the trigger: the trigger is
                    width:100% and vb.css loads after the Tailwind layers, so a
                    `w-[Npx]` on it would be inert and the control would size to
                    its content — jittering as the month name changes length. */}
                <div className="w-[7.5rem]">
                    <Select
                        value={selectedMonth}
                        onValueChange={onMonthChange}
                        aria-label="Report month"
                        className="vb-select__trigger--btn"
                    >
                        {filteredMonths.map((m) => (
                            <SelectItem key={m} value={m}>
                                {m}
                            </SelectItem>
                        ))}
                    </Select>
                </div>

                <div className="w-[5.5rem]">
                    <Select
                        value={selectedYear}
                        onValueChange={handleYearChange}
                        aria-label="Report year"
                        className="vb-select__trigger--btn"
                    >
                        {years.map((y) => (
                            <SelectItem key={y} value={y}>
                                {y}
                            </SelectItem>
                        ))}
                    </Select>
                </div>
            </div>
            ) : null}

            {showReRunButton && onReRun && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <VbButton
                                variant="secondary"
                                onClick={isFreeUser ? undefined : onReRun}
                                className={ICON_BTN}
                                disabled={isFreeUser}
                                aria-label="Re-run report"
                                title="Re-run report"
                            >
                                <RotateCw className="h-4 w-4" />
                            </VbButton>
                        </TooltipTrigger>
                        {isFreeUser && (
                            <TooltipContent>
                                <p>Upgrade your plan to use this action.</p>
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}

            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div>
                            <ShareReportDialog
                              reportId={reportId}
                              companyId={companyId}
                              companyName={companyName}
                              open={isFreeUser ? false : dialogOpen}
                              onOpenChange={isFreeUser ? () => {} : setDialogOpen}
                              trigger={
                                <VbButton
                                    variant="secondary"
                                    className={ICON_BTN}
                                    disabled={isFreeUser}
                                    aria-label="Share report"
                                    title="Share report"
                                >
                                    <Send className="h-4 w-4" />
                                </VbButton>
                              }
                            />
                        </div>
                    </TooltipTrigger>
                    {isFreeUser && (
                        <TooltipContent>
                            <p>Upgrade your plan to use this action.</p>
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>

            {showScheduleButton && onSchedule && (
                <VbButton variant="secondary" onClick={onSchedule}>
                    Schedule report
                    <CalendarClock className="h-4 w-4" />
                </VbButton>
            )}
        </div>
    );
};
